import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Package, CheckCircle2, Clock, XCircle, MinusCircle, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth, API } from "../App";
import { extractError } from "../lib/errors";
import { toast } from "sonner";

const STATUS_META = {
  compliant:      { label: "Conforme",    icon: CheckCircle2, color: "text-emerald-500", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  partial:        { label: "Parcial",     icon: Clock,        color: "text-amber-500",   badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  non_compliant:  { label: "No Conforme", icon: XCircle,      color: "text-red-500",     badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  not_applicable: { label: "No Aplica",   icon: MinusCircle,  color: "text-slate-400",   badge: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

const ControlRow = ({ control, response, onSave, onSuggest }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(response?.status || "");
  const [notes, setNotes] = useState(response?.notes || "");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(control.id, { status, notes });
    setSaving(false);
  };

  const handleSuggest = async () => {
    setAiLoading(true);
    const text = await onSuggest(control);
    if (text) setNotes((prev) => (prev ? `${prev}\n\n${text}` : text));
    setAiLoading(false);
  };

  const meta = STATUS_META[status];

  return (
    <div className="border border-border rounded-lg">
      <button
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 rounded-lg transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="text-[10px] flex-shrink-0">{control.section}</Badge>
            <span className="text-sm font-medium truncate">{control.title}</span>
            {meta && (
              <Badge variant="outline" className={`text-[10px] ${meta.badge} flex-shrink-0`}>
                <meta.icon className="w-2.5 h-2.5 mr-1" />
                {meta.label}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{control.description}</p>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground">{control.description}</p>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 text-sm w-56">
              <SelectValue placeholder="Estado..." />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_META).map(([val, m]) => (
                <SelectItem key={val} value={val}>
                  <div className="flex items-center gap-2">
                    <m.icon className={`w-3.5 h-3.5 ${m.color}`} />
                    {m.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Notas o referencia de evidencia..."
            className="text-sm h-20 resize-none"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={!status || saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button size="sm" variant="outline" onClick={handleSuggest} disabled={aiLoading}>
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              {aiLoading ? "Generando..." : "Sugerir con AI"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ZonaFrancaNORDOM477 = () => {
  const { token } = useAuth();
  const [controls, setControls] = useState([]);
  const [assessment, setAssessment] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [ctrlRes, assRes] = await Promise.all([
        axios.get(`${API}/zona-franca/controls/nordom477`),
        axios.get(`${API}/zona-franca/assessment/nordom477`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setControls(ctrlRes.data.controls || []);
      setAssessment(assRes.data.responses || {});
    } catch {
      toast.error("No se pudo cargar la evaluación NORDOM 477");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (controlId, body) => {
    try {
      const res = await axios.put(`${API}/zona-franca/assessment/nordom477/${controlId}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssessment((prev) => ({ ...prev, [controlId]: body }));
      toast.success(`Control actualizado — ${res.data.completion_pct}%`);
    } catch {
      toast.error("Error al guardar");
    }
  };

  const handleSuggest = async (control) => {
    try {
      const res = await axios.post(
        `${API}/zona-franca/ai/suggest`,
        {
          framework_code: "nordom477",
          control_id: control.id,
          control_title: control.title,
          control_description: control.description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Sugerencia generada");
      return res.data.suggestion;
    } catch (err) {
      toast.error(extractError(err, "No se pudo generar sugerencia"));
      return "";
    }
  };

  const answered = Object.keys(assessment).length;
  const total = controls.length;
  const compliant = Object.values(assessment).filter((r) => r.status === "compliant").length;
  const completionPct = total ? Math.round((answered / total) * 100) : 0;
  const compliancePct = total ? Math.round((compliant / total) * 100) : 0;

  // Group by section
  const sections = [...new Set(controls.map((c) => c.section))];

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#06b6d420", border: "1px solid #06b6d440" }}>
            <Package className="w-5 h-5 text-cyan-500" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold">NORDOM 477</h1>
            <p className="text-sm text-muted-foreground">Norma Dominicana de Calidad para productos fabricados en Zona Franca · INDOCAL</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">INDOCAL</Badge>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Evaluación", value: `${completionPct}%` },
          { label: "Conformidad", value: `${compliancePct}%` },
          { label: "Conformes", value: `${compliant}/${total}` },
        ].map((k) => (
          <Card key={k.label}><CardContent className="p-4">
            <p className="text-2xl font-bold text-cyan-500">{loading ? "—" : k.value}</p>
            <p className="text-xs text-muted-foreground">{k.label}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card><CardContent className="p-4">
        <div className="flex justify-between text-sm mb-1">
          <span>Progreso NORDOM 477</span>
          <span className="font-medium text-cyan-500">{completionPct}%</span>
        </div>
        <Progress value={completionPct} className="h-2" />
      </CardContent></Card>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : (
        <div className="space-y-4">
          {sections.map((sec) => {
            const secControls = controls.filter((c) => c.section === sec);
            return (
              <Card key={sec}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{sec}</CardTitle>
                  <CardDescription className="text-xs">{secControls.length} ítems</CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {secControls.map((ctrl) => (
                    <ControlRow key={ctrl.id} control={ctrl} response={assessment[ctrl.id]} onSave={handleSave} onSuggest={handleSuggest} />
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ZonaFrancaNORDOM477;
